(ns practitest.frontend.forms3.entity-form.forms
  (:require
   [clojure.data]
   [clojure.set]
   [practitest.frontend.components.forms :as forms]
   [practitest.frontend.components.notifications :as notifications]
   [practitest.frontend.subs :as subs]
   [practitest.frontend.version-updates.events :as version-events]
   [re-frame.core :as re-frame]))


;; Form layout looks like this
(comment
  {:header [{:field :name}]
   :left [{:field :description}]
   :right [{:field :status
            :permission ""}]})

;; A bit awkward, but we need to know form layout in generic event handlers to rebuild form state
(defmulti build-entity-form-layout
          (fn [form-id new? all-fields entity-fields entity]
            form-id))

(defn- get-field-value [base-entity field]
  (get base-entity field))

(defn- new-field-state
  [base-entity field]
  {:value    (get-field-value base-entity field)
   :dirty?   false
   :touched? false})

(defn flatten-layout [form-layout]
  (mapcat (fn [entry]
            (when-let [fields (:fields entry)]
              (map :field fields)))
          (vals form-layout)))

;; Reconcile form state with actual form presentation
(defn reconcile-form-state [db form-id new? entity-type entity]
  (let [layout (build-entity-form-layout form-id
                                         new?
                                         (:fields db)
                                         (get (:entity-fields db) entity-type)
                                         entity)
        form-fields  (flatten-layout layout)
        entity-fields (get-in db [:entity-fields entity-type])
        fields       (:fields db)]
    (when form-id
      (update-in db
                 [:form-states2 form-id]
                 (fn [form-state]
                   (let [form-state-fields (keys (:fields form-state))
                         [new-fields old-fields same-fields] (clojure.data/diff (set form-fields)
                                                                                (set form-state-fields))

                         form-state (reduce
                                      (fn [form-state field]
                                        (update form-state :fields dissoc field))
                                      form-state
                                      old-fields)

                         form-state (assoc form-state :base-entity entity)]

                     ;; New/Existing fields processing
                     (reduce
                       (fn [form-state new-field]
                         (let [entity-field (get entity-fields new-field)
                               field-spec   (get fields new-field)]
                           (update-in form-state [:fields new-field]
                                      ;; Here we assume that fields and entity-fields are already loaded
                                      (fn [existing-state]
                                        (if new?
                                          ;; Init new form fields with default values
                                          (if (nil? existing-state)
                                            (let [parent-default (when-let [parent-field-name (:parent_list_field_name field-spec)]
                                                                   (let [parent-entity-field (get entity-fields parent-field-name)]
                                                                     (:default parent-entity-field)))
                                                  initial-state {:value         (:default entity-field)
                                                                 :default-value (:default entity-field)}]
                                              (cond-> initial-state
                                                parent-default (assoc :dirty? true)))
                                            existing-state)

                                          ;; Editing form updates
                                          (if (nil? existing-state)
                                            ;; New field state
                                            (new-field-state entity new-field)

                                            ;; Existing field updates
                                            (if (or
                                                  (not= (:value existing-state) (get-field-value entity new-field))
                                                  (and (nil? (:value existing-state))
                                                     ;; It's possible we have a changed field with overriden value
                                                     (not (:dirty? existing-state))))
                                              ;; Assoc field value after entity is loaded/updated
                                              (assoc existing-state :value (get-field-value entity new-field))

                                              ;; TODO: handle updates from server
                                              existing-state)))))))
                       form-state
                       (clojure.set/union new-fields same-fields))))))))

(defn reset-form-state-with-entity
  [db base-entity form-id]
  (update-in db [:form-states2 form-id]
             (fn [form-state]
               (let [form-fields (keys (:fields form-state))]
                 (reduce
                   (fn [form-state field]
                     (assoc-in form-state [:fields field] (new-field-state base-entity field)))
                   (assoc form-state :base-entity base-entity)
                   form-fields)))))

;; Complicated flows:
;
; 1. <User1> starts creating new testset
; 2. Form is loaded with default values for that testset
; 3. <User2> concurrently updates field settings
;   3.a. Changes default value
;   3.b. Makes another field mandatory
;   3.c. Changes order of fields
;   3.d. Adds another field to testset
; How should we handle that ?
;

;; Form state representation
(comment

  ;; entity state - contains entity representation in different states
  :entity {:testset {

                     ;; remote entity representation - can be updated e.g. via WebSocket, readonly in our case
                     :remote {}

                     ;; base entity representation - how entity looked when we started editing, synced on-save
                     :base   {}}}

                     ;; NOTE: current entity representation is built from form-fields


  ;; form-props - passed as props
  {:id     :form-id
   :new?   true
   :edit?  false
   :layout {}}

  ;; form-state - theoretically we can store it in local-storage and then restore on page refresh
  ;; can work both with per-field saving or with separate "save" button
  :form-id {:loading?     false
            :initialized? true
            :saving?      false

            :fields       {:field-id

                           ;; field-state - part of form-state
                           {
                            :dirty?      true
                            :touched?    true

                            ;; Edit mode ?
                            :editing?    false

                            ;; Field saving pending
                            :saving?     false

                            ;; Had an error saving this particular field
                            :save-error? false

                            ;; Stored in field-state - need to be reconciled with entity
                            :value       "123"

                            ;; Default value (if present) that was used on that form
                            :default-value "default string"

                            ;; Errors are stored inside field state
                            :errors      []}}}




  ;; field-props
  {
   :field-id   :field-id
   :default    "Default value"
   :mandatory? false
   :readonly?  true
   :validators []})



(re-frame/reg-sub
  ::form-state
  (fn [db [_ form-id]]
    (get-in db [:form-states2 form-id])))

(re-frame/reg-sub
  ::dirty-fields
  (fn [[_ form-id]]
    (re-frame/subscribe [::form-state form-id]))
  (fn [form-state _]
    (filter :dirty? (vals (:fields form-state)))))

(re-frame/reg-sub
  ::modified-by-others?
  (fn [[_ form-id]]
    (re-frame/subscribe [::form-state form-id]))
  (fn [form-state _]
    (:touched? form-state)))

(re-frame/reg-sub
  ::form-dirty?
  (fn [[_ form-id]]
    (re-frame/subscribe [::dirty-fields form-id]))
  (fn [dirty-fields [_ _]]
    ;; Form is dirty if any field is dirty
    (seq dirty-fields)))

(defn reset-form-state
  [db form-id new? entity-type]
  ;; If the user cancels and the unsaved changes notification is shown,
  ;; dismiss it and show the regular critical update modal.
  (when (= (get-in db [:version-update :display-type]) :critical-with-unsaved-changes)
    (notifications/dismiss "version-update-notification")
    (re-frame/dispatch [::version-events/update-version-update-display-type :critical-modal]))

  ;; Empty state - reset fields values
  (-> db
      (update-in [:form-states2 form-id] dissoc :fields)
      (reconcile-form-state form-id
                            new?
                            entity-type
                            (when-not new?
                              (get-in db [:entity entity-type :current])))))

(re-frame/reg-event-db
  ::reset-form-state
  (fn [db [_ form-id new? entity-type]]
    (reset-form-state db form-id new? entity-type)))

(re-frame/reg-event-db
  ::set-form-state
  (fn [db [_ form-id form-state]]
    (assoc-in db [:form-states2 form-id] form-state)))

;(re-frame/reg-global-interceptor re-frame/debug)

;; Direct accessor for some event handlers
(defn get-field-state
  [db form-id field-id]
  (get-in db [:form-states2 form-id :fields field-id]))

(re-frame/reg-sub
  ::field-state
  (fn [[_ form-id]]
    (re-frame/subscribe [::form-state form-id]))
  (fn [form-state [_ _ field-id]]
    (get-in form-state [:fields field-id])))

;; For direct access from some handlers
(defn set-field-state
  [db [_ form-id field-id field-state]]
  (assoc-in db [:form-states2 form-id :fields field-id] field-state))

(re-frame/reg-event-db
  ::set-field-state
  set-field-state)

;; Do not use if not really necessary, skips validation and other features
;; Just sets value and dirty? flag in form state
(re-frame/reg-event-db
  ::set-field-value-internal
  (fn [db [_ form-id field-id value]]
    (update-in db
               [:form-states2 form-id :fields field-id]
               (fn [field-state]
                 (assoc field-state :value value
                                    :dirty? true)))))

(re-frame/reg-sub
  ::base-entity
  (fn [db [_ entity-type]]
    ;; TODO: reusing existing db path for now, fix later
    (get-in db [:entity entity-type :current])))

(re-frame/reg-sub
  ::remote-entity
  (fn [db [_ entity-type]]
    ;; TODO: reusing existing db path for now, fix later
    (get-in db [:entity entity-type :remote])))

(defn scroll-to-field [form-ref field-props {:keys [field-ref] :as field-state}]
  (let [
        ;; use field-ref to scroll if present
        node (if (and field-ref @field-ref)
               @field-ref
               ;; Use fallback lookup by field-id
               ;; TODO: cleanup this after field-ref is added to every field type
               ;; We need this form ref because of shadow DOM
               (when @form-ref
                 (.querySelector @form-ref (str "#" (name (:name field-props))))))]

    (when node
      (.scrollIntoView node #js {:block "center" :behavior "smooth"}))))

(defn entity->display-name
  [entity-type]
  (case entity-type
    :testset
    "Test Set"

    :milestone
    "Milestone"

    :instance
    "Instance"

    :test
    "Test"

    :issue
    "Issue"

    :requirement
    "Requirement"

    :et
    "Exploratory Test"

    :run
    "Run"

    ;else
    (str entity-type)))

(defn entity->namelabel
  [entity-type]
  (str (entity->display-name entity-type) " Name"))

;; Special field - issue status with transitions
(re-frame/reg-sub
  ::issues-statuses
  (fn [_]
    (re-frame/subscribe [::subs/options]))
  (fn [options]
    (:issues-statuses options)))

(re-frame/reg-sub
 ::tests-statuses
 (fn [_]
   (re-frame/subscribe [::subs/options]))
 (fn [options]
   (:tests-statuses options)))

(re-frame/reg-sub
  ::user-groups
  :<- [::subs/current-project]
  :<- [::subs/user-projects]
  (fn [[current-project user-projects] _]
    (some->> user-projects
             (filter #(= (:id (:project %)) (:id current-project)))
             first
             :user_groups)))

(defn get-mandatory-validator [field-type]
  (case field-type
    ;; Do not enforce mandatory checkbox
    :checkbox
    nil

    ;; TODO: more field types
    (:tags :date :date-time :folder_id)
    [::forms/value-present]

    ;; TODO: multilist fix this mess with values being string or set or vector
    :multilist
    [::forms/multilist-validator]

    :list
    [::forms/value-present]

    :user
    [::forms/user-selected]

    :num_of_results
    [::forms/xbot-num-files-range]

    ;; TODO: special validators for e.g. duration
    ;else
    [::forms/string-not-blank]))

;; Slightly changed from existing one
(re-frame/reg-sub
  ::field-props
  (fn [[_ entity field-id]]
    [(re-frame/subscribe [::subs/fields])
     (re-frame/subscribe [::subs/entity-fields entity])
     (re-frame/subscribe [::subs/users])
     (re-frame/subscribe [::subs/tags])
     (re-frame/subscribe [:practitest.frontend.kanban/lanes])
     (re-frame/subscribe [::subs/groups])
     (re-frame/subscribe [:practitest.frontend.folders/folders])
     (re-frame/subscribe [::issues-statuses])
     (re-frame/subscribe [::tests-statuses])
     (re-frame/subscribe [::user-groups])
     (re-frame/subscribe [::subs/use-test-workflow?])])
  (fn [[all-fields entity-fields users tags lanes groups folders issues-statuses tests-statuses user-groups use-test-workflow?]
       [_ entity namespaced-field]]
    (let [[field-ns field] (subs/parse-namespaced-field namespaced-field)
          ;; Special case for "name" field
          props (if (= namespaced-field :name)
                  {:name    :name
                   :custom? false
                   :system? true
                   :type    :text
                   :label   (entity->namelabel entity)
                   :placeholder (entity->namelabel entity)
                   :max-length 255
                   ;; special flag to indicate that it's a title field without label shown
                   :title?  true
                   :id      "name"}
                  (get all-fields field))
          entity-props (if (or (= namespaced-field :name)
                               ;; Issue status is mandatory as well, but not marked as such
                               (= namespaced-field :issue_status))
                         {:mandatory? true}
                         (get entity-fields field))
          mandatory? (or (= namespaced-field :scenario)
                         (:mandatory? entity-props))
          field-type (:type props)]
      (merge
       (cond-> props
         field-ns (update :label (partial subs/namespaced-label field-ns))
         (= :user field-type) (assoc :possible-values
                                     (if (= :assigned_to field)
                                              ;; "Assigned to" supports both users and groups
                                       (vec (concat users groups))
                                              ;; Other (custom) user fields support only users
                                       users))

          ;; Issue status specific transitions support     
         (= :issue_status namespaced-field) (->
                                              (assoc :list-transitions (:transitions_groups issues-statuses))
                                              (assoc :user-groups user-groups))

         (and use-test-workflow?
             (= :test_status namespaced-field)) (->
                                                   (assoc :list-transitions (:transitions_groups tests-statuses))
                                                   (assoc :user-groups user-groups))

         (= :folder_id field-type) (assoc :possible-values folders
                                          :folder-type (case entity
                                                         :test
                                                         "QtestsFolder"
                                                         :testset
                                                         "SuitesFolder"
                                                                ;;default
                                                         "Unknown"))
                ;; TODO: fix this on backend ?
         (= :kanban_lane field-type) (assoc :possible-values (mapv #(-> %
                                                                        (assoc :lane_id (:id %)))
                                                                   lanes))
         (= :memo field-type) (assoc :users users)
         (= :tags field-type) (assoc :possible-values tags)
                ;; Override field type for preconditions
         (= :preconditions namespaced-field) (assoc :type :memo))
       entity-props
       (cond-> {:field-id namespaced-field
                :field    namespaced-field}
         mandatory?
         (assoc :validators (get-mandatory-validator field-type))

         ;; Set URL validator on URL fields
         (= field-type :url)
         (update :validators (fnil conj []) ::forms/url-valid))))))

(defn clear-linked-lists [db [_ form-id field]]
  (let [fields (:fields db)
        field-spec (get fields field)

        ;; will be nil e.g. for kanban
        children (when-let [id (:id field-spec)]
                   (filter #(= (:parent_list_id %) id) (vals fields)))]

    (reduce
      (fn [db item]
        (-> db
            (set-field-state [::set-field-state form-id (:name item) {:dirty? true
                                                                         :value  nil}])
            ;; recursively descend
            (clear-linked-lists [::clear-linked-lists form-id (:name item)])))
      db
      children)))

(re-frame/reg-event-db
  ::clear-linked-lists
  clear-linked-lists)
